import { ComponentRef, Injectable, ViewChild, ViewContainerRef } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { catchError, map, mergeMap, of, tap } from 'rxjs'
import { CommonService } from 'src/app/services/common/common.service'
import {
  forgot_password_failed,
  forgot_password_success,
  login_failed,
  login_success,
  reset_password_with_password_failed,
  reset_password_with_password_success,
  start_forgot_password,
  start_login,
  start_login_with_otp,
  start_reset_password_with_password,
  start_sso_login
} from '../action/login.action'
import { AppState } from '../state/app.state'
import { AuthService } from '../../services/common/auth.service'
import { LoginService } from 'src/app/services/login/login.service'
import { start_initializing, update_password_auto_generated } from '../action/initialize.action'
import { ForgotPasswordComponent } from 'src/app/components/forgot-password/forgot-password.component'

@Injectable()
export class LoginEffects {
  @ViewChild('targetContainer', { read: ViewContainerRef }) targetContainer: ViewContainerRef
  targetComponentRef: ComponentRef<ForgotPasswordComponent>

  constructor(
    private actions$: Actions,
    private loginService: LoginService,
    private cs: CommonService,
    private appState: Store<AppState>
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_login),
      mergeMap((action) =>
        this.loginService.login(action.email, action.password).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message)
            this.cs.showError(errorMsg || 'INVALID EMAILID OR PASSWORD', 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )

  login_with_otp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_login_with_otp),
      mergeMap((action) =>
        this.loginService.loginUsingOTP(action.email, action.otp).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            this.cs.showError('Invalid or expired OTP', 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )

  sso_login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_sso_login),
      mergeMap(({ id_token, companyDomain }) =>
        this.loginService.ssoLogin(id_token, companyDomain).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message)
            this.cs.showError(errorMsg || 'SSO login failed', 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )

  loginRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(login_success),
        tap((action) => {
          this.appState.dispatch(start_initializing({ comingFromLogin: true }))
        })
      )
    },
    { dispatch: false }
  )

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_forgot_password),
      mergeMap(({ email, callback }) =>
        this.loginService.callForgotPasswordAPI(email).pipe(
          map((response: any) => {
            return forgot_password_success({ callback })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message)
            this.cs.showError(errorMsg || 'Something went wrong. Please try again later.', '', true)
            return of(forgot_password_failed(error))
          })
        )
      )
    )
  )

  forgotPasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(forgot_password_success),
        tap(({ callback }) => {
          this.cs.showSuccess('We will send you an email if we find your account.')
          callback()
        })
      )
    },
    { dispatch: false }
  )

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_reset_password_with_password),
      mergeMap(({ oldPassword, newPassword, callback }) =>
        this.loginService.callResetPasswordWithOldPasswordAPI(oldPassword, newPassword).pipe(
          map((response: any) => {
            return reset_password_with_password_success({ callback })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message)
            this.cs.showError(errorMsg || 'Something went wrong. Please try again later.', '', true)
            return of(reset_password_with_password_failed(error))
          })
        )
      )
    )
  )

  resetPasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(reset_password_with_password_success),
        tap(({ callback }) => {
          this.cs.showSuccess('Password has been successfully changed')
          callback()
          this.appState.dispatch(update_password_auto_generated({ val: false }))
        })
      )
    },
    { dispatch: false }
  )
}
